/**
 * Centralized constants for the Milim Rishonot (מילים ראשונות) application.
 * Import from here to avoid magic strings/numbers scattered across the codebase.
 */

// ============================================================================
// STORAGE KEYS - Used for localStorage
// ============================================================================
export const STORAGE_KEYS = {
  /** User progress data (words learned, games played, etc.) */
  PROGRESS: 'talking-animals-progress',
  /** Earned sticker IDs */
  STICKERS: 'talking-animals-stickers',
  /** Theme preference (light/dark/system) */
  THEME: 'talk-game-theme',
  /** Language preference (he/en) */
  LANGUAGE: 'talk-game-language',
  /** Settings (theme + language) for Firestore sync */
  SETTINGS: 'talk-game-settings',
  /** Whether the user has seen the onboarding tutorial */
  ONBOARDING_SEEN: 'talk-game-onboarding-seen',
} as const;

// ============================================================================
// TIMING CONSTANTS
// ============================================================================
export const TIMING = {
  /** Debounce delay for saving to localStorage (ms) */
  DEBOUNCE_SAVE_MS: 300,
  /** How long to show "locked" message on stickers (ms) */
  LOCKED_MESSAGE_TIMEOUT_MS: 2500,
  /** API request timeout (ms) */
  API_TIMEOUT_MS: 30000,
  /** Print view render delay before triggering print (ms) */
  PRINT_DELAY_MS: 150,
  /** Session complete narration delay (ms) */
  SESSION_COMPLETE_NARRATION_DELAY_MS: 500,
} as const;

// ============================================================================
// GAME CONSTANTS
// ============================================================================
export const GAME = {
  /** Number of recent words to exclude from random selection (cooldown) */
  COOLDOWN_SIZE: 4,
  /** Default number of words per session */
  SESSION_LENGTH: 10,
  /** Number of images to preload ahead of current word */
  IMAGE_PRELOAD_COUNT: 3,
} as const;

// Sticker collection unlock thresholds are defined in src/data/sticker-collections.json
// and accessed via stickerCollections from src/data/stickers.ts

// ============================================================================
// UI CONSTANTS
// ============================================================================
export const UI = {
  /** Placeholder image for missing word images */
  PLACEHOLDER_IMAGE: '/images/placeholder.svg',
  /** Default PIN for parent area (fallback if env var not set) */
  DEFAULT_PARENT_PIN: '1234',
} as const;

// ============================================================================
// FIRESTORE CONSTANTS
// ============================================================================
export const FIRESTORE = {
  /** Firestore collection name for user data */
  USERS_COLLECTION: 'users',
  /** Firestore document field names */
  FIELDS: {
    PROGRESS: 'progress',
    STICKERS: 'stickers',
    SETTINGS: 'settings',
    METADATA: 'metadata',
  },
} as const;

// ============================================================================
// FREEMIUM CONSTANTS
// ============================================================================
export const FREEMIUM = {
  /** Maximum number of words available to free users */
  FREE_WORD_LIMIT: 80,
  /** Game modes available to free users */
  FREE_GAME_MODES: ['smart-mix'] as const,
  /** Sticker collections available to free users */
  FREE_STICKER_COLLECTIONS: ['dinosaurs'] as const,
  /** Phonetic categories included in the free tier (Lips group + Labiodental) */
  FREE_PHONETIC_CATEGORIES: [
    'Bilabial (Stop)',
    'Bilabial (Nasal)',
    'Labiodental',
  ] as const,
  /** localStorage key for premium status cache */
  PREMIUM_CACHE_KEY: 'talk-game-premium-status',
  /** Price display string (fallback when checkout unavailable) */
  PRICE_DISPLAY: '₪34.90',
} as const;

// ============================================================================
// LEMON SQUEEZY CONSTANTS
// ============================================================================
export const LEMONSQUEEZY = {
  /** Lemon Squeezy store ID */
  STORE_ID: '308645',
  /** Product variant IDs (each plan has one default variant) */
  VARIANTS: {
    MONTHLY: '1374796',
  },
  /** Checkout URLs for each plan (from Lemon Squeezy dashboard) */
  CHECKOUT_URLS: {
    MONTHLY: 'https://milimrishonot.lemonsqueezy.com/checkout/buy/1bf1500b-a929-459f-9ff8-824eef8ce2cd',
  },
  /** Firestore field path for premium data */
  FIRESTORE_PREMIUM_FIELD: 'premium',
} as const;

// ============================================================================
// API CONSTANTS
// ============================================================================
export const API = {
  /** Base URL for API endpoints */
  BASE_URL: '/api',
  /** Default timeout for API requests (ms) */
  TIMEOUT_MS: 30000,
  /** Max file size for audio upload (bytes) - 25MB */
  MAX_AUDIO_FILE_SIZE: 25 * 1024 * 1024,
} as const;
